import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrent, useFetch } from "../../hooks";
import { useParams } from "react-router-dom";
import {
  MwInputText,
  MwSelector,
  MwSpinnerButton,
  NoDataFound,
  TagList,
  Pagination,
} from "../../components/ui";
import ExpensesListItem from "./ExpensesListItem";

const ExpensesList = () => {
  const [t] = useTranslation("global");
  const params = useParams();
  const { currentTenantId, currentCompanyId, currentBranchId, currentLangId } =
    useCurrent();
  const canFetchContext = Boolean(
    currentTenantId && currentCompanyId && currentBranchId
  );
  const baseApiUrl = canFetchContext
    ? `${process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL}/expenses/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
    : null;

  const { data: vendorsResult } = useFetch(
    canFetchContext
      ? `${process.env.REACT_APP_VENDORS_API_SERVER_BASE_URL}/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null
  );
  const { data: taxesResult } = useFetch(
    canFetchContext
      ? `${process.env.REACT_APP_VAT_WEB_BASE_URL}/sales/taxes/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null
  );
  const {
    data: expensesResult,
    loading: loadingResult,
    refreshHandler,
  } = useFetch(baseApiUrl, { cache: false, persist: false });

  const [vendorsList, setVendorsList] = useState([]);
  useEffect(() => {
    if (vendorsResult?.data) {
      const _list = vendorsResult.data.map((item) => {
        const fullnameObj = item.fullname?.find((name) => name.lang === "ar");
        return {
          label: fullnameObj?.text || "",
          value: item?._id || "",
          lang: currentLangId,
        };
      });
      setVendorsList(_list);
    }
  }, [vendorsResult, currentLangId]);

  const [filters, setFilters] = useState({
    tagList: [],
    tDate: "",
    fDate: "",
    vendors: "",
    code: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const filteredData = useMemo(() => {
    if (!expensesResult?.data) return [];

    return [...expensesResult.data]
      .sort((a, b) => {
        const dateA = new Date(a?.expense_date || 0).getTime();
        const dateB = new Date(b?.expense_date || 0).getTime();
        if (dateA !== dateB) return dateB - dateA;
        const codeA = Number(a?.code || 0);
        const codeB = Number(b?.code || 0);
        if (!Number.isNaN(codeA) && !Number.isNaN(codeB) && codeA !== codeB) {
          return codeB - codeA;
        }
        return Number(b?.id || 0) - Number(a?.id || 0);
      })
      .filter(
      (item) =>
        (!String(filters.code || "").trim() ||
          String(item?.code || "")
            .toLowerCase()
            .includes(String(filters.code || "").trim().toLowerCase()) ||
          String(item?.desc || "")
            .toLowerCase()
            .includes(String(filters.code || "").trim().toLowerCase())) &&
        (!filters.fDate || item.expense_date >= filters.fDate) &&
        (!filters.tDate || item.expense_date <= filters.tDate) &&
        (!filters.vendors ||
          (item.vendor_id &&
            filters.vendors.value &&
            item.vendor_id.toString() === filters.vendors.value.toString())) &&
        (filters.tagList.length === 0 ||
          filters.tagList.every((tag) => item.tags.includes(tag)))
    );
  }, [expensesResult, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const taxTypeById = useMemo(() => {
    const list = taxesResult?.data || [];
    return list.reduce((acc, tax) => {
      acc[String(tax.id)] = {
        label: currentLangId === "ar" ? tax.tax_name_ar : tax.tax_name_en,
        value: Number(tax.tax_value),
      };
      return acc;
    }, {});
  }, [taxesResult, currentLangId]);

  const handleFilterChange = (key, value) => {
    setCurrentPage(1);
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  };

  return (
    <div className="main flex relative justify-between w-full">
      {loadingResult ? (
        <div className="loading w-full h-[70vh] absolute flex justify-center items-center">
          <MwSpinnerButton label={""} isFullCenter={true} />
        </div>
      ) : (
        <>
          {expensesResult?.data?.length > 0 ? (
            <div className="flex flex-col justify-center items-center w-full gap-2">
              <div className="filter mt-1 px-3 py-3 pb-6 w-full  bg-[#F8FAFC] justify-between rounded-md flex flex-wrap gap-4 items-end">
                <div className="w-80">
                  <MwInputText
                    inputType="text"
                    label={t("expenses.expensesNumber")}
                    placeholder="ابحث بالسند أو الوصف"
                    value={filters.code}
                    onChange={(e) => handleFilterChange("code", e.target.value)}
                    classNameInput="w-full"
                  />
                </div>
                <div className="flex gap-2 flex-row justify-between items-center">
                  <div className="w-48">
                    <MwInputText
                      inputType="date"
                      label={t("Filter.fDate")}
                      value={filters.fDate}
                      onChange={(e) =>
                        handleFilterChange("fDate", e.target.value)
                      }
                    />
                  </div>
                  <div className="w-48">
                    <MwInputText
                      inputType="date"
                      label={t("Filter.tDate")}
                      value={filters.tDate}
                      onChange={(e) =>
                        handleFilterChange("tDate", e.target.value)
                      }
                    />
                  </div>
                  <div className="w-48">
                    <MwSelector
                      label={t("expenses.vendor")}
                      initalValue={t("invoice.selectVendor")}
                      dataType="labelValue"
                      _data={vendorsList}
                      withAddNew={false}
                      withSearch={false}
                      invalid=""
                      invalidMsg=""
                      className="w-full"
                      selectedItem={filters.vendors}
                      setSelectedItem={(val) =>
                        handleFilterChange("vendors", val)
                      }
                    />
                  </div>
                  <div className="w-48 min-w-[200px]">
                    <TagList
                      tagList={filters.tagList}
                      setTagList={(tags) => handleFilterChange("tagList", tags)}
                      label={t("Filter.Tags")}
                      withLabel={true}
                      classNamePrivate="w-full"
                    />
                  </div>
                </div>
              </div>
              {filteredData.length > 0 ? (
                <div className="w-[100%] mt-2">
                  <>
                    <div
                      dir={currentLangId === "ar" ? "rtl" : "ltr"}
                      className="w-[98%] mx-auto max-h-[calc(100vh-290px)] overflow-y-auto thin-scrollbar rounded-lg"
                    >
                      <div className="sticky top-0 z-10 flex w-full gap-1 bg-[#EBF9D9] font-bold py-1 px-1 rounded-t-lg text-[#261759] text-sm">
                        <div className="w-[110px] shrink-0 text-start ps-2">
                          {t("expenses.expensesNumber")}
                        </div>
                        <div className="flex-[1.6] min-w-[280px] text-start">
                          {t("expenses.expensesDescription")}
                        </div>
                        <div className="w-[135px] shrink-0 text-center">
                          {t("expenses.expensesAmountwithoutvat")}
                        </div>
                        <div className="flex-[1.1] min-w-[220px] text-center">
                          {t("expenses.Vattype")}
                        </div>
                        <div className="w-[95px] shrink-0 text-center">
                          {t("expenses.Vatexp")}
                        </div>
                        <div className="w-[135px] shrink-0 text-center">
                          {t("expenses.expensesAmountwithvat")}
                        </div>
                        <div className="w-[122px] shrink-0 text-center">
                          {t("expenses.expensesDate")}
                        </div>
                        <div className="w-[96px] shrink-0 text-center"></div>
                      </div>
                      {currentItems.map((item, index) => (
                        <ExpensesListItem
                          key={item.id}
                          item={item}
                          index={(currentPage - 1) * itemsPerPage + index}
                          refreshHandler={refreshHandler}
                          tenant={params.tenant}
                          taxTypeById={taxTypeById}
                        />
                      ))}
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </>
                </div>
              ) : (
                <div className="  h-[calc(100vh-248px)] mt-[-25px]  w-[98%] mx-auto ">
                  <NoDataFound msg={t("Salaries.validate")} />
                </div>
              )}
            </div>
          ) : (
            <div className=" h-[calc(100vh-168px)]   w-[98%] mx-auto ">
              <NoDataFound msg={t("expenses.validate")} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExpensesList;
